import { IBaseStore } from '../types'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import {
  EChainStatus,
  EOnboardingMilestoneStatus,
  EOrigin,
  EUserType,
  IAdminTask,
  IAdminTaskHistory,
  IBillingHistory,
  IChain,
  IChainComments,
  IChainsFilterDTO,
  ICommission,
  ICreateChainDTO,
  IFacilityMonthlyBillingBatch,
  IOnboarding,
  IOnboardingMilestone,
  IOnboardingMilestoneTemplate,
  ITokenPayload,
  ITrainingMilestoneTemplate,
  IUser,
  IUserFacilityMonthlyBillingBatch,
  IZuntaInvoice
} from '@zunta/configurations'
import Api from '@/api'

const groupBy = require('lodash.groupby')

export interface IChainState {
  chains: IChain[];
  total: number;
  lastQuery: IChainsFilterDTO;
  adminTasks: IAdminTask[];
  comments: IChainComments[];
  onboardingTemplates: IOnboardingMilestoneTemplate[];
  trainingTemplates: ITrainingMilestoneTemplate[];
  onboardingData: Array<{
    onboarding: IOnboarding,
    milestones: IOnboardingMilestone[]
  }>;
  adminTaskHistories: IAdminTaskHistory[];
  onboadingMilestoneStatistic: any[];
  billingBatches: IFacilityMonthlyBillingBatch[];
  totalBatches: number;
  billingInvoices: IZuntaInvoice[];
  totalInvoices: number;
  billingHistory: IBillingHistory[];
  selectedBatch: IFacilityMonthlyBillingBatch | IUserFacilityMonthlyBillingBatch | null;
  batchUsersCommissions: any[];
  usersCommissions: ICommission[];
  usersCommissionsTotal: number;
  facilitiesCountStatistic: any[];
  billingBatchPagination: any;
  onboardingStatistics: any;
}

export interface IChainStore extends IBaseStore {
  state: IChainState;
}

@Module({
  name: 'chain',
  namespaced: true
})
export class ChainModule extends VuexModule {
  chains: IChain[] = [];
  chain: IChain | null = null;
  total: number = 0;
  lastQuery: IChainsFilterDTO = {};
  adminTasks: IAdminTask[] = [];
  onboardingTemplates: IOnboardingMilestoneTemplate[] = []
  comments: IChainComments[] = [];
  taskDetailesType: string = 'chain'
  onboardingData: Array<{
    onboarding: IOnboarding,
    milestones: IOnboardingMilestone[]
  }> = [];
  adminTaskHistories: IAdminTaskHistory[] = [];
  trainingTemplates: ITrainingMilestoneTemplate[] = []
  onboadingMilestoneStatistic: any[] = [];
  billingBatches: IFacilityMonthlyBillingBatch[] = [];
  totalBatches: number = 0;

  billingInvoices: IZuntaInvoice[] = [];
  totalInvoices: number = 0;

  billingHistory: IBillingHistory[] = [];

  selectedBatch: IFacilityMonthlyBillingBatch | IUserFacilityMonthlyBillingBatch | null = null;

  batchUsersCommissions: any[] = [];

  usersCommissions: ICommission[] = [];
  usersCommissionsTotal: number = 0;
  facilitiesCountStatistic: any[] = [];

  billingBatchPagination: any = null;

  onboardingStatistics: any = null

  @Mutation
  SET_ONBOARDING_STATISTICS (data: any) {
    this.onboardingStatistics = data
  }

  @Mutation
  SET_BILLING_BATCH_PAGINATION (pagination: any) {
    this.billingBatchPagination = pagination
  }

  @Mutation
  SET_FACILITIES_COUNT_STATISTIC (statistic: any[]) {
    this.facilitiesCountStatistic = statistic
  }

  @Mutation
  SET_USERS_COMMISSIONS_TOTAL (total: number) {
    this.usersCommissionsTotal = total
  }

  @Mutation
  SET_USERS_COMMISSIONS (commissions: ICommission[]) {
    this.usersCommissions = commissions
  }

  @Mutation
  SET_BATCH_USERS_COMMISSIONS (commissions: ICommission[]) {
    const localCommissions: any[] = []
    const commissionsGroupByUser = groupBy(commissions, (commission: ICommission) => `${(commission.user as IUser).id}-${commission.commissionsSettings ? 'settings' : 'nosettings'}`)
    Object.keys(commissionsGroupByUser).forEach((key: string, index: number) => {
      const userCommissions: ICommission[] = commissionsGroupByUser[key]
      const commission: any = {
        key: `${key}-${index}`,
        batch: userCommissions[0].batch,
        user: userCommissions[0].user,
        amount: 0,
        settings: userCommissions[0].commissionsSettings,
        facilitiesCount: 0
      }
      if (commission.settings) {
        const facilities: number[] = []
        userCommissions.forEach((userCommission: ICommission) => {
          if (!facilities.includes(userCommission.facility as number)) {
            facilities.push(userCommission.facility as number)
          }
          commission.amount = commission.amount + (+(userCommission.amount || 0))
        })
        commission.facilitiesCount = facilities.length
        localCommissions.push(commission)
      } else {
        userCommissions.forEach((userCommission: ICommission, index: number) => {
          localCommissions.push({
            ...commission,
            key: `${commission.key}-${index}`,
            amount: +(userCommission.amount || 0)
          })
        })
      }
    })
    this.batchUsersCommissions = [
      ...localCommissions
    ]
  }

  @Mutation
  SET_SELECTED_BATCH (batch: IFacilityMonthlyBillingBatch | IUserFacilityMonthlyBillingBatch) {
    this.selectedBatch = batch
  }

  @Mutation
  SET_BILLING_HISTORY (history: IBillingHistory[]) {
    this.billingHistory = history
  }

  @Mutation
  SET_BILLING_INVOICES (invoices: IZuntaInvoice[]) {
    this.billingInvoices = invoices
  }

  @Mutation
  SET_BILLING_INVOICES_TOTAL (total: number) {
    this.totalInvoices = total
  }

  @Mutation
  SET_BILLING_BATCHES (batches: IFacilityMonthlyBillingBatch[]) {
    this.billingBatches = batches
  }

  @Mutation
  SET_BILLING_BATCHES_TOTAL (total: number) {
    this.totalBatches = total
  }

  @Mutation
  SET_ONBOARDING_MILESTONE_STATISTIC (data: any[]) {
    this.onboadingMilestoneStatistic = data
  }

  @Mutation
  SET_TRAINING_TEMPLATES (templates: ITrainingMilestoneTemplate[]) {
    this.trainingTemplates = templates
  }

  @Mutation
  SET_ADMIN_TASK_HISTORIES (adminTaskHistories: IAdminTaskHistory[]) {
    this.adminTaskHistories = adminTaskHistories
  }

  @Mutation
  SET_ONBOARDING_DATA (data: any) {
    this.onboardingData = data
  }

  @Mutation
  SET_CHAIN_COMMENTS (comments: IChainComments[]) {
    const groupedComments = []
    const grouped = groupBy(comments, 'baseComment')
    for (let i = 0; i < comments.length; i++) {
      const comment = comments[i]

      if (comment.baseComment) continue

      const commentWithReply = {
        ...comment,
        reply: grouped[comment.id] || []
      }
      groupedComments.push(commentWithReply)
    }
    this.comments = groupedComments
  }

  @Mutation
  ADD_ADMIN_TASK (task: IAdminTask) {
    this.adminTasks.push(task)
  }

  @Mutation
  SET_ONBOARDING_TEMPLATES (templates: IOnboardingMilestoneTemplate[]) {
    this.onboardingTemplates = templates
  }

  @Mutation
  SET_ADMIN_TASKS (adminTasks: IAdminTask[]) {
    this.adminTasks = adminTasks
  }

  @Mutation
  UPDATE_LAST_QUERY (query: IChainsFilterDTO) {
    this.lastQuery = query
  }

  @Mutation
  SET_CHAIN (chain: IChain | null) {
    this.chain = chain
  }

  @Mutation
  UPDATE_CHAIN (chainData: Partial<IChain>) {
    Object.assign(this.chain as IChain, chainData)
  }

  @Mutation
  UPDATE_CHAINS_TOTAL (total: number) {
    this.total = total
  }

  @Mutation
  ADD_CHAIN (chain: IChain) {
    this.chains = [...this.chains, chain]
  }

  @Mutation
  UPDATE_CHAINS_LIST (chains: IChain[]) {
    this.chains = chains
  }

  @Mutation
  UPDATE_CHAIN_STATUS (input: { id: number, status: EChainStatus }) {
    const chain = this.chains.find((chain: IChain) => chain.id === input.id)
    if (chain) {
      chain.status = input.status
    }
  }

  @Action
  async removeBatch (batchId: number) {
    await Api.billing.removeBatch(batchId)
  }

  @Action
  async getMilestonesStatistics () {
    const data = await Api.onboarding.getOnboardingMilestoneStatistic()
    this.SET_ONBOARDING_STATISTICS(data)
  }

  @Action
  async fetchBillingBatchPagination (options: { batch: number | string, user?: number | string }) {
    const pagination = await Api.billing.getBillingBatchPagination(options)
    this.SET_BILLING_BATCH_PAGINATION(pagination)
  }

  @Action
  async fetchFaciltiiesCountStatistic (facilities: number[] | string[]) {
    const statistic = await Api.billing.getFacilitiesCountStatistic(facilities)
    this.SET_FACILITIES_COUNT_STATISTIC(statistic)
  }

  @Action
  async fetchCommissionsByFilters (filters: any) {
    const { commissions, total } = await Api.billing.getCommissionsByFilters(filters)
    this.SET_USERS_COMMISSIONS(commissions)
    this.SET_USERS_COMMISSIONS_TOTAL(total)
  }

  @Action
  async fetchBatchUsersCommissions (batchId: number | string) {
    const commissions = await Api.billing.getBatchUsersCommissions(batchId)
    this.SET_BATCH_USERS_COMMISSIONS(commissions)
  }

  @Action
  async fetchBillingBatch (batchId: number | string) {
    const batch = await Api.billing.getBillingBatch(batchId)
    this.SET_SELECTED_BATCH(batch)
  }

  @Action
  async fetchUserBillingBatch (options: { batchId: number | string, userId: number | string }) {
    const batch = await Api.billing.getUserBillingBatch(options.batchId, options.userId)
    this.SET_SELECTED_BATCH(batch)
  }

  @Action
  async fetchBillingHistory (invoiceId: number | string) {
    const history = await Api.billing.getBillingHistory(invoiceId)
    this.SET_BILLING_HISTORY(history)
  }

  @Action
  async addBillingHistory (data: Partial<IBillingHistory>) {
    await Api.billing.addBillingHistory(data)
  }

  @Action
  async rebillInvoice (invoiceId: number | string) {
    return Api.billing.rebillInvoice(invoiceId)
  }

  @Action
  async billInvoice (invoiceId: number | string) {
    return Api.billing.billInvoice(invoiceId)
  }

  @Action
  async refundInvoice (input: { invoice: number | string, options: any }) {
    return Api.billing.refundInvoice(input.invoice, input.options)
  }

  @Action
  async billInvoices (batchId: number | string) {
    const res = await Api.billing.billInvoices(batchId)
    return res === 'OK'
  }

  @Action
  async uploadBatchCosts (input: { batchId: number | string, file: File }) {
    return Api.billing.uploadCosts(input.batchId, input.file)
  }

  @Action
  async getBillingInvoices (filters: any) {
    const { invoices, total } = await Api.billing.getInvoicesByFilters(filters)
    this.SET_BILLING_INVOICES(invoices)
    this.SET_BILLING_INVOICES_TOTAL(total)
  }

  @Action
  async getBillingBatchesList (filters: any) {
    const { batches, total } = await Api.billing.getBillingBatchesByFilters(filters)
    this.SET_BILLING_BATCHES(batches)
    this.SET_BILLING_BATCHES_TOTAL(total)
  }

  @Action
  async fetchOnboardingMilestoneStatistic () {
    const data = await Api.onboarding.getOnboardingMilestoneStatistic()
    this.SET_ONBOARDING_MILESTONE_STATISTIC(data)
  }

  @Action
  async updateChainOnboarding (input: { chainId: number | string, data: any }) {
    await Api.chain.updateOnboardingChain(input.chainId, input.data)
  }

  @Action
  async fetchAdminTaskHistories (taskId: number | string) {
    const taskHistories = await Api.onboarding.getTaskHistories(taskId)
    this.SET_ADMIN_TASK_HISTORIES(taskHistories)
  }

  @Action
  async addAdminTaskHistoryComment (input: { taskId: number | string, data: any }) {
    await Api.onboarding.addAdminTaskHistoryComment(input.taskId, input.data)
    await this.fetchAdminTaskHistories(input.taskId)
  }

  @Action
  async changeMilestoneStatus (input: { milestone: number | string, status: EOnboardingMilestoneStatus }) {
    await Api.onboarding.changeMilestoneStatus(input.milestone, input.status)
    await this.fetchOnboardingData((this.chain as IChain).id)
  }

  @Action
  async fetchOnboardingData (chainId: number | string) {
    const data = await Api.onboarding.getOnboardingData(chainId)
    this.SET_ONBOARDING_DATA(data)
  }

  @Action
  async updateOnboarding (input: { onboardingId: number | string, data: Partial<IOnboarding> }) {
    await Api.onboarding.updateOnboarding(input.onboardingId, input.data)
    await this.fetchOnboardingData((this.chain as IChain).id)
  }

  @Action
  async changeAdminTaskStatus (input: { id: number | string, data: any }) {
    await Api.onboarding.changeAdminTaskStatus(input.id, input.data)
    await this.fetchAdminTaskHistories(input.id)
  }

  @Action
  async fetchChainComments (chainId: number | string) {
    const comments = await Api.chain.getChainComments(chainId)
    this.SET_CHAIN_COMMENTS(comments)
  }

  @Action
  async updateChainComment (input: { id: number | string, text: string }) {
    await Api.chain.updateChainComment(input.id, input.text)
    this.fetchChainComments((this.chain as IChain).id)
  }
  @Action
  async removeChainComment (id: string | number) {
    await Api.chain.removeChainComment(id)
    this.fetchChainComments((this.chain as IChain).id)
  }

  @Action
  async addChainComment (input: { chainId: number | string, data: { text: string, baseComment: number | null } }) {
    await Api.chain.addChainComment(input.chainId, input.data)
    this.fetchChainComments(input.chainId)
  }

  @Action
  async rescheduleTask (input: { id: number | string, date: number, note: string }) {
    await Api.onboarding.rescheduleTask(input.id, input.date, input.note)
    await this.fetchAdminTaskHistories(input.id)
  }

  @Action
  async changeChainCommentPin (input: { commentId: number, pin: boolean }) {
    await Api.chain.changeChainCommentPin(input.commentId, input.pin)
  }

  @Action
  async changeAllowToViewSystem (input: { id: number | string, AllowToViewSystem: boolean }) {
    await Api.chain.changeAllowToViewSystem(input.id, input.AllowToViewSystem)
    this.UPDATE_CHAIN({ AllowToViewSystem: input.AllowToViewSystem })
  }

  @Action
  async saveOnboardingTemplatesOrder (templates: IOnboardingMilestoneTemplate[]) {
    await Api.onboarding.saveTemplateOrder(templates)
    await this.fetchOnboardingTemplates()
  }

  @Action
  async editOnboardingTemplate (input: { id: number, data: Partial<IOnboardingMilestoneTemplate> }) {
    await Api.onboarding.editOnboardingTemplate(input.id, input.data)
    await this.fetchOnboardingTemplates()
  }

  @Action
  async deleteOnboardingTemplate (id: number | string) {
    await Api.onboarding.deleteOnboardingTemplate(id)
    await this.fetchOnboardingTemplates()
  }

  @Action
  async createOnboardingTemplate (data: { parent?: number, name: string }) {
    await Api.onboarding.createMilestoneTemplate(data)
    await this.fetchOnboardingTemplates()
  }

  @Action
  async fetchOnboardingTemplates () {
    const templates = await Api.onboarding.getOnboardingTemplates()
    this.SET_ONBOARDING_TEMPLATES(templates)
  }

  @Action
  async addUserAdminTask (data: any) {
    await Api.onboarding.addUserAdminTask(data)
  }

  @Action
  async addAdminTask (data: any) {
    await Api.onboarding.addAdminTask(data)
  }

  @Action
  async getChainsList (query: IChainsFilterDTO) {
    const { chains, total } = await Api.chain.getChainsList(query)
    this.UPDATE_LAST_QUERY(query)
    this.UPDATE_CHAINS_TOTAL(total)
    this.UPDATE_CHAINS_LIST(chains || [])
  }

  @Action
  async getChainById (id: string | number) {
    if (!id || id === -1) {
      return
    }
    const chain = await Api.chain.getChain(id)
    this.SET_CHAIN(chain)
  }

  @Action
  async fetchChain (input: { id: string | number, relations?: string[] }) {
    const chain = await Api.chain.getChain(input.id, input.relations)
    this.SET_CHAIN(chain)
  }

  @Action
  async saveTrainingTemplatesOrder (templates: ITrainingMilestoneTemplate[]) {
    await Api.training.saveTemplateOrder(templates)
    await this.fetchTrainingTemplates()
  }

  @Action
  async editTrainingTemplate (input: { id: number, data: Partial<ITrainingMilestoneTemplate> }) {
    await Api.training.editOnboardingTemplate(input.id, input.data)
    await this.fetchTrainingTemplates()
  }

  @Action
  async deleteTrainingTemplate (id: number | string) {
    await Api.training.deleteOnboardingTemplate(id)
    await this.fetchTrainingTemplates()
  }

  @Action
  async createTrainingTemplate (data: { parent?: number, name: string }) {
    await Api.training.createMilestoneTemplate(data)
    await this.fetchTrainingTemplates()
  }
  @Action
  async fetchTrainingTemplates () {
    const templates = await Api.onboarding.fetchTrainingTemplates()
    this.SET_TRAINING_TEMPLATES(templates)
  }

  @Action
  async fetchUserAdminTasks (userId: number | string) {
    const tasks = await Api.onboarding.getUserAdminTasks(userId)
    this.SET_ADMIN_TASKS(tasks)
  }

  @Action
  async fetchAdminTasks (chainId: number | string) {
    const tasks = await Api.onboarding.getAdminTasks(chainId)
    this.SET_ADMIN_TASKS(tasks)
  }

  @Action
  async residentCanEnterChain (residentId: string | number) {
    if (!residentId) {
      return false
    }
    return Api.chain.residentCanEnterChain(residentId)
  }

  @Action
  async createChain (data: ICreateChainDTO) {
    const response = await Api.chain.createChain(data)
    if (!response.statusCode) {
      this.getChainsList(this.lastQuery)
      return null
    }
    return response
  }

  @Action
  async updateChain (input: {id: number, data: Partial<IChain> }) {
    const response = await Api.chain.updateChain(input.id, input.data)
    if (response === 'OK') {
      this.getChainsList(this.lastQuery)
      return null
    }
    return response
  }

  @Action
  async updateUserAdminTask (input: { id: number | string, data: any, template: number | undefined }) {
    await Api.onboarding.updateUserTask(input.id, input.data, input.template)
    await this.fetchAdminTaskHistories(input.id)
  }

  @Action
  async updateAdminTask (input: { id: number | string, data: any, template: number | undefined }) {
    await Api.onboarding.updateTask(input.id, input.data, input.template)
    await this.fetchAdminTaskHistories(input.id)
  }

  @Action
  async updateStatus (input: { id: number, status: EChainStatus }) {
    const response = await Api.chain.updateStatus(input.id, input.status)
    if (response === 'OK') {
      this.UPDATE_CHAIN_STATUS(input)
      return null
    }
    return null
  }

  @Action
  async updateFacilitiesData (input: { chainId: number }) {
    const response = await Api.chain.updateFacilitiesData(input.chainId)
    if (response === 'OK') {
      // this.updateChain(input.chainId)
      return null
    }
    return null
  }

  @Action
  async deleteChain (id: number) {
    return Api.chain.deleteChain(id)
  }

  @Action
  async createOnboardingChain (data: any) {
    await Api.chain.createOnboardingChain(data)
  }

  get getChain () {
    return this.chain
  }
  get isPcc () {
    return this.chain &&
        (
            this.chain.billingSoftware === EOrigin.PCC ||
            this.chain.billingSoftware === 'BOTH'
        )
  }

  @Action
  async checkSftpUser (username: string) {
    const checkResponse: string = await Api.chain.checkSftpUser(username)
    return checkResponse.toLowerCase().trim().includes('ok')
  }

  @Action
  async downloadSftpUserKey (input: { username: string, token: string }) {
    const { username, token } = input
    const res = await Api.chain.createSftpUser(username, token)
    if (res.statusCode === 500) {
      throw new Error('Server Error')
    }
    // @ts-ignore
    const url = window.URL.createObjectURL(new Blob([res]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${username}`)
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  @Action
  async getCanEnterChain (userData: ITokenPayload): Promise<boolean> {
    if (userData.type === EUserType.ZUNTA) {
      return true
    }
    if (userData.chain) {
      await this.getChainById(userData.chain)
      return !!this.chain && this.chain.status === EChainStatus.ACTIVE
    }
    if (userData.residents && userData.residents.length) {
      return this.residentCanEnterChain(userData.residents[0])
    }
    return false
  }
}
